import { getPageLabel } from 'components/Chat/instructor-hugo/pageConfig';
import { ChatRole, INSTRUCTOR_HUGO_CHAT_TYPE } from 'utils/chat/chatConstants';
import { ScopeTypes } from 'utils/chat/instructorHugoContext';
import { v4 as uuid } from 'uuid';

export const ActiveThreadReconciliationIntervalMs = 2000;

export const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

export const getFirstName = ({ auth, currentUser }) => {
    const name = currentUser?.first_name || auth?.first_name || currentUser?.display_name || auth?.display_name || '';
    return name.trim().split(/\s+/)[0] || '';
};

export const createLocalChatShell = ({ auth, scope }) => {
    const timestamp = Date.now();
    return {
        id: null,
        local_id: uuid(),
        active: true,
        created_on: timestamp,
        modified_on: timestamp,
        chat_type: INSTRUCTOR_HUGO_CHAT_TYPE,
        owner_account_id: auth?.owner_account_id,
        scope_type: scope.type,
        course_id: scope.course_id,
        assignment_id: scope.assignment_id,
        is_processing: false,
        messages: [],
        assistant_interactions: [],
        messages_version: 0,
        read_state: [],
    };
};

export const getRouteChatKey = ({ auth, sessionContext }) => {
    const { pageContext, scope } = sessionContext;

    return [
        auth?.owner_account_id || '',
        scope.type || '',
        scope.course_id || '',
        scope.assignment_id || '',
        pageContext.page_type || '',
        pageContext.active_tab || '',
    ].join(':');
};

export const buildThreadFilter = ({ auth }) => ({
    active: true,
    chat_type: INSTRUCTOR_HUGO_CHAT_TYPE,
    owner_account_id: auth?.owner_account_id,
});

export const getChatTitle = (chat) => chat?.title?.trim() || '';

export const getThreadTitle = (chat) => {
    const title = getChatTitle(chat);
    if (title) return title;

    const latestMessage = chat.latest_message || (chat.messages || []).slice(-1)[0];
    return latestMessage?.message_text?.trim();
};

export const shouldShowPageSuggestions = (chat) => {
    return !chat?.id && !(chat?.messages || []).some((message) => message.role === ChatRole.USER);
};

export const getThreadScopeLabel = (chat, t) => {
    const scopes = [];

    if ([ScopeTypes.COURSE, ScopeTypes.ASSIGNMENT].includes(chat.scope_type)) {
        scopes.push(chat.course_name || t('ui.course', 'Course'));
    }

    if (chat.scope_type === ScopeTypes.ASSIGNMENT) {
        scopes.push(chat.assignment_name || t('ui.assignment', 'Assignment'));
    }

    if (chat.scope_type === ScopeTypes.USER) {
        const pageType = chat.page_type || chat.latest_message?.page_context?.page_type;
        scopes.push(getPageLabel(pageType));
    }

    return scopes.join(' > ');
};

export const buildThreadSummaryFromChat = ({ chat, chatId }) => {
    const latestMessage = chat.latest_message || (chat.messages || []).slice(-1)[0] || null;
    const modifiedOn = latestMessage?.modified_on || latestMessage?.created_on || chat.modified_on || Date.now();

    return {
        ...chat,
        id: chatId,
        is_processing: true,
        latest_message: latestMessage,
        messages: [],
        modified_on: modifiedOn,
    };
};

export const upsertThreadSummary = (threadSummaries, threadSummary) => {
    if (!threadSummary?.id) return threadSummaries;

    return [threadSummary, ...threadSummaries.filter((thread) => thread.id !== threadSummary.id)];
};

export const hasMatchingAssistantMessage = (chat, assistantMessage) => {
    if (!chat || !assistantMessage) return false;

    const requestedInteractionId = assistantMessage.requested_interaction?.id;
    const messageText = assistantMessage.message_text?.trim();

    return (chat.messages || []).some((message) => {
        if (message.active === false || message.role !== ChatRole.ASSISTANT) return false;
        if (message.id && message.id === assistantMessage.id) return true;
        if (requestedInteractionId && message.requested_interaction?.id === requestedInteractionId) return true;
        return Boolean(messageText && message.message_text?.trim() === messageText);
    });
};
